import { z } from "zod";
import { Created, Id, IsoDate, OptionalText } from "./common";
import { defineRoutes } from "./define";
import { PROBLEMS } from "./problem";

/** Mirrors core/modules/tasks/domain/task-enums.ts (the contract is the client-facing copy). */
export const TASK_TYPES = [
  "message_post_visit",
  "check_24h",
  "event_reminder",
  "offer_service",
  "update_plan",
  "request_media",
  "other",
] as const;
export const TASK_PRIORITIES = ["low", "medium", "high"] as const;
export const TASK_STATUSES = ["new", "in_progress", "done", "cancelled"] as const;
export const TaskType = z.enum(TASK_TYPES);
export const TaskPriority = z.enum(TASK_PRIORITIES);
export const TaskStatus = z.enum(TASK_STATUSES);

/** Mirrors core/modules/tasks/application/task-filter.ts. */
export const TaskStatusFilter = z.enum(["open", "all", ...TASK_STATUSES]);
export const TaskDueFilter = z.enum(["all", "overdue", "today", "week", "none"]);
export const TASK_TRANSITIONS = ["start", "complete", "cancel", "reopen"] as const;
export const TaskTransition = z.enum(TASK_TRANSITIONS);

export const TaskBoardItem = z.object({
  id: Id,
  title: z.string(),
  patientId: Id,
  patientName: z.string(),
  episodeId: Id.nullable(),
  episodeTitle: z.string().nullable(),
  type: TaskType,
  priority: TaskPriority,
  status: TaskStatus,
  dueDate: z.string().nullable(),
  /** dueDate is in the past and the task is still open. */
  overdue: z.boolean(),
  assigneeId: z.string().nullable(),
  assigneeName: z.string().nullable(),
  comment: z.string().nullable(),
});
export type TaskBoardItem = z.infer<typeof TaskBoardItem>;

export const TaskStatusSummary = z.object({
  open: z.number().int(),
  new: z.number().int(),
  in_progress: z.number().int(),
  done: z.number().int(),
  cancelled: z.number().int(),
  total: z.number().int(),
});

export const TaskFilter = z.object({
  status: TaskStatusFilter,
  priority: z.union([TaskPriority, z.literal("all")]),
  type: z.union([TaskType, z.literal("all")]),
  due: TaskDueFilter,
  assigneeId: z.string(),
  page: z.number().int(),
  pageSize: z.number().int(),
});

export const TaskBoardData = z.object({
  filter: TaskFilter,
  items: z.array(TaskBoardItem),
  /** Total matching rows across all pages. */
  total: z.number().int(),
  summary: TaskStatusSummary,
  assignees: z.array(z.object({ id: z.string(), name: z.string() })),
});
export type TaskBoardData = z.infer<typeof TaskBoardData>;

/** The task after a write — what the board row needs to update in place. */
export const TaskSnapshot = z.object({
  id: Id,
  patientId: Id,
  episodeId: Id.nullable(),
  title: z.string(),
  type: TaskType,
  priority: TaskPriority,
  status: TaskStatus,
  dueDate: z.string().nullable(),
  assigneeId: z.string().nullable(),
  comment: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TaskSnapshot = z.infer<typeof TaskSnapshot>;

export const ListTasksQuery = z.object({
  status: TaskStatusFilter.optional(),
  priority: TaskPriority.optional(),
  type: TaskType.optional(),
  due: TaskDueFilter.optional(),
  assigneeId: z.string().max(64).optional(),
  page: z.coerce.number().int().min(1).optional(),
  /** Capped by the use-case (5..100, default 30). */
  pageSize: z.coerce.number().int().min(1).optional(),
});

export const CreateTaskBody = z.object({
  patientId: Id,
  episodeId: Id.optional(),
  title: z.string().trim().min(1, "Назва задачі обовʼязкова").max(200),
  type: TaskType.optional(),
  priority: TaskPriority.optional(),
  dueDate: IsoDate.optional(),
});
export type CreateTaskBody = z.infer<typeof CreateTaskBody>;

/** Mirrors core/modules/tasks/validation/task-schema.ts — the form submits every field. */
export const UpdateTaskBody = z.object({
  title: z.string().trim().min(1, "Назва задачі обовʼязкова").max(200),
  type: TaskType.optional(),
  priority: TaskPriority.optional(),
  dueDate: IsoDate.optional(),
  assigneeId: OptionalText(64),
  comment: OptionalText(2000),
});
export type UpdateTaskBody = z.infer<typeof UpdateTaskBody>;

export const TransitionTaskBody = z.object({
  transition: TaskTransition,
  /** Free-text reason, applied on cancel. */
  comment: OptionalText(2000),
});
export type TransitionTaskBody = z.infer<typeof TransitionTaskBody>;

export const tasks = defineRoutes({
  list: {
    method: "GET",
    path: "/tasks",
    summary: "The filterable task board (one page + status totals + assignees)",
    query: ListTasksQuery,
    responses: { 200: TaskBoardData, 400: PROBLEMS[400], 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  create: {
    method: "POST",
    path: "/tasks",
    summary: "Create a task for a patient (optionally within an episode)",
    body: CreateTaskBody,
    responses: {
      201: Created,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      422: PROBLEMS[422],
    },
  },
  update: {
    method: "PATCH",
    path: "/tasks/:taskId",
    summary: "Edit an open task (status moves only via transition)",
    params: z.object({ taskId: Id }),
    body: UpdateTaskBody,
    responses: {
      200: TaskSnapshot,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
  transition: {
    method: "POST",
    path: "/tasks/:taskId/transition",
    summary: "Move a task: start | complete | cancel (with reason) | reopen",
    params: z.object({ taskId: Id }),
    body: TransitionTaskBody,
    responses: {
      200: TaskSnapshot,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
});
